import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Loader2, Plus, Minus, Zap, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { formatCurrency, TOPUP_PRICING } from '@/lib/subscriptionFeatures';

interface TopupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  currentTier: string;
  topupCountThisMonth: number;
  maxTopupsThisMonth: number;
  onSuccess?: () => void;
}

export function TopupDialog({
  open,
  onOpenChange,
  companyId,
  currentTier,
  topupCountThisMonth,
  maxTopupsThisMonth,
  onSuccess,
}: TopupDialogProps) {
  const { toast } = useToast();
  const [quantity, setQuantity] = useState(1); // Quantity of 100-conversation packs
  const [isProcessing, setIsProcessing] = useState(false);

  const conversationAmount = quantity * TOPUP_PRICING.minimumAmount;
  const priceIdr = quantity * TOPUP_PRICING.per100Conversations;
  
  const isFreeUser = currentTier === 'free';
  const remainingTopups = maxTopupsThisMonth === -1 ? -1 : maxTopupsThisMonth - topupCountThisMonth;
  const canTopup = maxTopupsThisMonth === -1 || topupCountThisMonth < maxTopupsThisMonth;

  const handleQuantityChange = (delta: number) => {
    const newQuantity = quantity + delta;
    if (newQuantity >= 1 && newQuantity <= 100) {
      setQuantity(newQuantity);
    }
  };

  const handlePurchase = async () => {
    setIsProcessing(true);
    try {
      const { data, error } = await supabase.rpc('process_topup', {
        p_company_id: companyId,
        p_amount: conversationAmount,
        p_price_idr: priceIdr,
      });

      if (error) throw error;

      // Cast data to expected shape
      const result = data as Record<string, any> | null;

      if (result?.success) {
        toast({
          title: 'Top-up Berhasil!',
          description: `${conversationAmount} percakapan AI telah ditambahkan ke akun Anda.`,
        });
        onOpenChange(false);
        onSuccess?.();
      } else {
        throw new Error(result?.error || 'Top-up failed');
      }
    } catch (error: any) {
      console.error('Top-up error:', error);
      toast({
        title: 'Top-up Gagal',
        description: error.message === 'max_topups_reached' 
          ? 'Batas top-up bulanan sudah tercapai. Upgrade ke Professional untuk top-up tanpa batas.'
          : 'Gagal memproses top-up. Silakan coba lagi.',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Calculate expiry date (end of current month)
  const expiryDate = new Date();
  expiryDate.setMonth(expiryDate.getMonth() + 1);
  expiryDate.setDate(0); // Last day of current month
  const expiryString = expiryDate.toLocaleDateString('id-ID', { 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric' 
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            Beli Percakapan AI
          </DialogTitle>
          <DialogDescription>
            Tambah kuota percakapan AI untuk bulan ini
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Free user warning */}
          {isFreeUser && (
            <div className="flex items-start gap-3 p-3 bg-warning/10 border border-warning/20 rounded-lg">
              <AlertCircle className="h-5 w-5 text-warning shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-warning">Batas Top-up Free Tier</p>
                <p className="text-muted-foreground mt-1">
                  Paket Free hanya bisa top-up maksimal 3x per bulan. 
                  Anda sudah melakukan {topupCountThisMonth} top-up.
                </p>
                {remainingTopups > 0 && (
                  <Badge variant="outline" className="mt-2">
                    Sisa {remainingTopups} top-up bulan ini
                  </Badge>
                )}
              </div>
            </div>
          )}

          {/* Quantity selector */}
          <div className="space-y-3">
            <Label>Jumlah Paket (100 percakapan/paket)</Label>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="icon"
                onClick={() => handleQuantityChange(-1)}
                disabled={quantity <= 1}
              >
                <Minus className="h-4 w-4" />
              </Button>
              <div className="flex-1 text-center">
                <Input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
                  className="text-center text-lg font-semibold"
                  min={1}
                  max={100}
                />
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={() => handleQuantityChange(1)}
                disabled={quantity >= 100}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Summary */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Percakapan AI</span>
              <span className="font-medium">{conversationAmount.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Harga per 100</span>
              <span>{formatCurrency(TOPUP_PRICING.per100Conversations)}</span>
            </div>
            <div className="border-t pt-3 flex justify-between">
              <span className="font-medium">Total</span>
              <span className="text-lg font-bold text-primary">
                {formatCurrency(priceIdr)}
              </span>
            </div>
            <div className="text-xs text-muted-foreground text-center pt-2">
              Berlaku hingga {expiryString} • Tidak bisa diperpanjang
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Batal
          </Button>
          <Button 
            onClick={handlePurchase} 
            disabled={isProcessing || !canTopup}
          >
            {isProcessing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Memproses...
              </>
            ) : (
              <>
                <Zap className="h-4 w-4 mr-2" />
                Beli Sekarang
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
